// Hideo - online saving of profiles (services in services.ts). Every profile has "Save online" and "Load online": the
// user picks the service (connecting it there if needed). Works only from the online site: the services don't allow
// signing in from a page opened as a file (file://).
// Autosave (autosync.ts): right after every change, the profile is saved to every connected service where it has already
// been saved or from which it was loaded (CS(id).links[profile]).
// The Profiles window, the service picker and the list of files to load are Vue (ProfilesDialog.vue, CloudPickDialog.vue,
// DriveFilesDialog.vue; actions in src/stores/cloud.ts). Here: saving a profile online, linking files, the autosave pause.
// Ported from the legacy cloud/drive.js (verbatim copy in tests/fixtures/legacy/drive.js, golden tests in tests/saving*.test.ts).
import { t, tv } from '../../i18n'
import { useDataStore } from '../../stores/data'
import { useSyncStore } from '../../stores/sync'
import { useUiStore } from '../../stores/ui'
import { useCloudStore } from '../../stores/cloud'
import { syncToast, uiConfirm } from '../../stores/notices'
import { CS, saveDriveState, type CloudError } from './common'
import { CLOUDS, CLOUD_ORDER } from './services'
import { driveUploadMedia, mediaRetry } from './media'
import { hhmm, renderSyncUi } from './autosync'
import { fileStamp, fmtWhen, refreshWarn, setSync, syncOf } from '../storage/sync'
import { editRevNow, profileData, profileFileText, saveAppData, writeProfileData } from '../storage/save'

/** Result of an online save */
type SaveResult = 'ok' | 'conflict' | 'auth' | 'busy' | 'error'

// ===== Save online =====
/** Saves the profile to its file on the service (created the first time). If there is a version saved by another device
 *  and not yet seen here, asks first. auto = autosave (autosync.ts): no windows or confirmations.
 *  Returns 'ok' | 'conflict' (online there is a version not yet seen here) | 'auth' (sign-in expired) | 'busy' | 'error' */
export async function driveSave(id: string, auto: boolean, pid: string): Promise<SaveResult> {
  const store = useDataStore(), sync = useSyncStore(), cloud = useCloudStore()
  const setDriveStatus = (text: string) => cloud.setStatus(text)
  const p = store.profiles.list.find(x => x.id === id), P = CLOUDS[pid], S = CS(pid); if (!p) return 'error'; if (sync.busy) return 'busy'
  sync.busy = true
  const names = { name: p.name, service: P.name }   // plain text (textContent): no esc()
  const label = auto ? t('Autosaving "{name}" to {service}…', names) : t('Saving "{name}" to {service}…', names)
  try {
    if (!(await P.token(!auto))) { if (auto) return 'auth'; throw new Error(t('Your {service} sign-in has expired.', { service: P.name })) }
    setDriveStatus(label)
    const fileName = `Hideo - ${p.name}.json`   // stored name on the service: never translated
    let remote = S.links[id] ? await P.meta(S.links[id]) : null
    if (!remote) remote = await P.findProfile(fileName, id)
    const seen = S.seen[id] || ''
    if (remote && remote.version && remote.version !== seen) {
      const when = remote.savedAt || remote.modified
      if (auto) { setDriveStatus(''); return 'conflict' }   // autosync.ts asks which version to keep
      // uiConfirm escapes the message: params stay plain text
      const msg = remote.savedOn
        ? t('There is a version of "{name}" on {service} ({when} from the {device}) that you have not loaded on this device yet.\n\nOverwrite it with the data on this device? If not, use "Load online".', { name: p.name, service: P.name, when: fmtWhen(when), device: tv(remote.savedOn) })
        : t('There is a version of "{name}" on {service} ({when}) that you have not loaded on this device yet.\n\nOverwrite it with the data on this device? If not, use "Load online".', { name: p.name, service: P.name, when: fmtWhen(when) })
      if (!(await uiConfirm(msg, { ok: t('Overwrite'), cancel: t("Don't save") }))) { setDriveStatus(''); return 'error' }
    }
    // First images and voice notes (Immagini and Vocali folders, media.ts), then the profile file that lists them in data.media
    const d = await profileData(id)
    if (await driveUploadMedia(d, setDriveStatus, pid)) { if (id === store.profiles.current) saveAppData(d, true); else await writeProfileData(id, d) }
    setDriveStatus(label)
    // Changes made while the file is uploading stay "to save" and go with the next save (editRev, save.ts)
    const rev = editRevNow(), stamp = fileStamp(), text = await profileFileText(id, stamp)
    const res = await P.saveProfile(remote && remote.id, fileName, text, { ...stamp, profileId: id })
    linkDriveFile(pid, id, res.id, res.version)
    setSync(id, { dirty: id === store.profiles.current && editRevNow() !== rev, file: { ...stamp, kind: 'online-salvato', provider: pid } })
    setAutoPaused('', pid); mediaRetry()
    sync.AS.state = 'ok'; sync.AS.at = Date.now(); renderSyncUi()
    if (!auto) syncToast(t('Saved to {service}', { service: P.name }))   // discreet notice: no thumbs-up for saves
    setDriveStatus(auto ? t('"{name}" saved to {service} (automatic, {time})', { name: p.name, service: P.name, time: hhmm(Date.now()) })
      : t('"{name}" saved to {service}', { name: p.name, service: P.name }))
    return 'ok'
  } catch (e) {
    if (auto) { console.error(e); setDriveStatus(''); return e && (e as CloudError).status === 401 ? 'auth' : 'error' }
    cloud.error(e); return 'error'
  }
  finally { sync.busy = false }
}
/** A profile just loaded from a service (applyLoadedFile): its save state names the service, and its file there is
 *  linked to it (autosave, conflict, Load online) */
export function linkLoaded(pid: string, id: string, fileId: string, version?: string): void {
  setSync(id, { file: { ...syncOf(id).file, provider: pid } })
  linkDriveFile(pid, id, fileId, version)
}
/** Links a file on a service to a profile: a file is linked to only one profile on this device (per service) */
export function linkDriveFile(pid: string, id: string, fileId: string, version?: string): void {
  const S = CS(pid)
  Object.keys(S.links).forEach(k => { if (S.links[k] === fileId && k !== id) delete S.links[k] })
  S.links[id] = fileId; if (version) S.seen[id] = version; saveDriveState()
}

// ===== Autosave: right after every change, in autosync.ts =====
/** The autosave pause message of a service ('' = resumed). All of them together (useSyncStore().paused) are the dot on
 *  the profile button and the Profiles window's notice */
export function setAutoPaused(msg: string, pid: string): void {
  const sync = useSyncStore()
  sync.autoPause[pid] = msg || ''
  const all = CLOUD_ORDER.map(k => sync.autoPause[k]).filter(Boolean).join(' ')
  if (sync.paused === all) return; sync.paused = all; refreshWarn(); renderDriveUi()
}

/** Online state changed: the sync indicator and the Profiles window */
export function renderDriveUi(): void { renderSyncUi(); useUiStore().refresh() }
